import { useState, useEffect } from 'react';
import { Save } from 'lucide-react';
import { AreaResponse, CreateAreaData, UpdateAreaData, AREA_PRESET_TYPES, getAreaPresetTypeLabel, type AreaPresetType } from '../../lib/areas';
import { PresetResponse } from '../../lib/presets';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { TimePicker } from '@/components/ui/time-picker';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

interface AreaFormModalProps {
  area: AreaResponse | null;
  siteControllerId: string;
  presets?: PresetResponse[]; // Presets for this area (only used when editing)
  onSubmit: (data: CreateAreaData | UpdateAreaData) => void;
  onClose: () => void;
  isSubmitting: boolean;
}

export default function AreaFormModal({
  area,
  siteControllerId,
  presets = [],
  onSubmit,
  onClose,
  isSubmitting,
}: AreaFormModalProps) {
  const isEditing = !!area;
  const [curfewError, setCurfewError] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    type: 'user' as AreaPresetType,
    currentPresetId: '',
    curfewStart: '',
    curfewEnd: '',
  });

  useEffect(() => {
    if (area) {
      setFormData({
        name: area.name,
        address: area.address !== null && area.address !== undefined ? area.address.toString() : '',
        type: area.type || 'user',
        currentPresetId: area.currentPresetId || '',
        curfewStart: area.curfewStart ?? '',
        curfewEnd: area.curfewEnd ?? '',
      });
    } else {
      setFormData({
        name: '',
        address: '',
        type: 'user' as AreaPresetType,
        currentPresetId: '',
        curfewStart: '',
        curfewEnd: '',
      });
    }
    setCurfewError(null);
  }, [area]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (formData.type === 'light') {
      const start = formData.curfewStart.trim();
      const end = formData.curfewEnd.trim();
      const startSet = !!start;
      const endSet = !!end;
      if (startSet !== endSet) {
        setCurfewError('Either set both curfew times, or leave both empty.');
        return;
      }
    }
    setCurfewError(null);
    const payload: CreateAreaData | UpdateAreaData = {
      name: formData.name,
      address: formData.address ? parseInt(formData.address) : undefined,
      type: formData.type,
      currentPresetId: formData.currentPresetId || null,
      curfewStart:
        formData.type === 'light'
          ? formData.curfewStart.trim()
            ? formData.curfewStart.trim()
            : null
          : null,
      curfewEnd:
        formData.type === 'light'
          ? formData.curfewEnd.trim()
            ? formData.curfewEnd.trim()
            : null
          : null,
    };

    if (!isEditing) {
      (payload as CreateAreaData).siteControllerId = siteControllerId;
    }

    onSubmit(payload);
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[425px] max-w-[90vw] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEditing ? 'Edit Area' : 'Create Area'}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
        <div className="space-y-2">
            <Label htmlFor="type">
              Type
            </Label>
            <Select
              value={formData.type}
              onValueChange={(value) =>
                setFormData((prev) => ({
                  ...prev,
                  type: value as AreaPresetType,
                  ...(value !== 'light' ? { curfewStart: '', curfewEnd: '' } : {}),
                }))
              }
            >
              <SelectTrigger id="type">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {AREA_PRESET_TYPES.map((type) => (
                  <SelectItem key={type} value={type}>
                    {getAreaPresetTypeLabel(type)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              Defaults to User. Lighting curfew can only be set for <span className="font-medium">Light</span> areas.
              <span className="font-medium"> System</span> / <span className="font-medium">Hidden</span> areas are
              synced from PDEG and filtered from most lists, but can be set here when editing.
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="name">
              Name *
            </Label>
            <Input
              id="name"
              type="text"
              required
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="address">
              Device Address
            </Label>
            <Input
              id="address"
              type="number"
              min="0"
              max="255"
              value={formData.address}
              onChange={(e) => {
                const value = e.target.value;
                if (value === '' || (parseInt(value) >= 0 && parseInt(value) <= 255)) {
                  setFormData({ ...formData, address: value });
                }
              }}
              placeholder="0-255"
            />
            <p className="text-xs text-muted-foreground">Device address (0-255)</p>
          </div>

          {formData.type === 'light' && (
            <div className="space-y-2">
              <Label>Lighting curfew (local time)</Label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label htmlFor="areaCurfewStart" className="text-xs text-muted-foreground">
                    Start
                  </Label>
                  <TimePicker
                    id="areaCurfewStart"
                    value={formData.curfewStart}
                    onChange={(v) =>
                      setFormData({ ...formData, curfewStart: v })
                    }
                    placeholder="Curfew start"
                    minuteGranularity="minute"
                  />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="areaCurfewEnd" className="text-xs text-muted-foreground">
                    End (Next day)
                  </Label>
                  <TimePicker
                    id="areaCurfewEnd"
                    value={formData.curfewEnd}
                    onChange={(v) =>
                      setFormData({ ...formData, curfewEnd: v })
                    }
                    placeholder="Curfew end"
                    minuteGranularity="minute"
                  />
                </div>
              </div>
              {curfewError && (
                <p className="text-xs text-destructive">{curfewError}</p>
              )}
              <p className="text-xs text-muted-foreground">
                Optional (24h). Open the picker to set times; use Clear inside to remove.
              </p>
            </div>
          )}

          {isEditing && presets && presets.length > 0 && (
            <div className="space-y-2">
              <Label htmlFor="currentPresetId">
                Current Preset
              </Label>
              <Select
                value={formData.currentPresetId || "__none__"}
                onValueChange={(value) => setFormData({ ...formData, currentPresetId: value === "__none__" ? "" : value })}
              >
                <SelectTrigger id="currentPresetId">
                  <SelectValue placeholder="None" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">None</SelectItem>
                  {presets.map((preset) => (
                    <SelectItem key={preset.id} value={preset.id}>
                      {preset.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">Select the active preset for this area</p>
            </div>
          )}

          <div className="flex justify-end gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting}
            >
              <Save className="h-4 w-4 mr-2" />
              {isSubmitting ? 'Saving...' : isEditing ? 'Update' : 'Create'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
